import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { partnerOfficeLocationsApi } from '../../api/partner-contacts-offices-bank';
import { ApiError } from '../../api/client';
import { DataTable } from '../../components/DataTable';
import { IconActionButton } from '../../components/IconActionButton';
import { Check } from 'lucide-react';
import { useToast } from '../../components/Toast';

const OFFICE_TYPE_LABELS: Record<string, string> = {
  headquarters: 'Headquarters', main_office: 'Main Office', regional_office: 'Regional Office',
  state_office: 'State Office', field_office: 'Field Office', sub_office: 'Sub-office', warehouse: 'Warehouse',
};

export function PartnerOfficesTab({ partnerId }: { partnerId: number }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const officesQuery = useQuery({ queryKey: ['partner-offices', partnerId], queryFn: () => partnerOfficeLocationsApi.list(partnerId) });
  const { register, handleSubmit, reset } = useForm<{ officeType: string; address: string; country: string; state: string; lga: string; phone: string; email: string }>();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['partner-offices', partnerId] });
  const createMutation = useMutation({
    mutationFn: (values: Record<string, string>) => partnerOfficeLocationsApi.create(partnerId, values),
    onSuccess: () => { showSuccess('Office location added.'); invalidate(); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add office location.'),
  });
  const verifyMutation = useMutation({ mutationFn: (id: number) => partnerOfficeLocationsApi.verify(id), onSuccess: () => { showSuccess('Verified.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });

  return (
    <div className="panel">
      <div className="panel-body">
        {officesQuery.data && (
          <DataTable
            rows={officesQuery.data.data}
            rowKey={(o) => o.id}
            emptyMessage="No office locations on file yet."
            columns={[
              { header: 'Type', render: (o) => OFFICE_TYPE_LABELS[o.office_type] ?? o.office_type },
              { header: 'Address', render: (o) => o.address ?? '-' },
              { header: 'State', render: (o) => o.state ?? '-' },
              { header: 'Country', render: (o) => o.country ?? '-' },
              { header: 'Status', render: (o) => o.office_status },
              { header: 'Verification', render: (o) => o.verification_status === 'verified' ? 'Verified' : 'Unverified' },
              { header: '', render: (o) => o.verification_status !== 'verified' && <IconActionButton icon={Check} label="Verify" onClick={() => verifyMutation.mutate(o.id)} /> },
            ]}
          />
        )}

        <h3>Add Office Location</h3>
        <form className="inline-form" onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
          <label>
            Type
            <select {...register('officeType', { required: true })}>
              <option value="">- choose -</option>
              {Object.entries(OFFICE_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label>Address<input {...register('address')} /></label>
          <label>State<input {...register('state')} /></label>
          <label>Country<input {...register('country')} /></label>
          <label>LGA<input {...register('lga')} /></label>
          <label>Phone<input {...register('phone')} /></label>
          <label>Email<input type="email" {...register('email')} /></label>
          <button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? 'Adding…' : 'Add'}</button>
        </form>
      </div>
    </div>
  );
}
